import { createTheme } from '@mui/material'

export const colors = {
  // Base
  softBlack: '#231F20',
  black: '#000',
  white: '#fff',
  accent: '#8E3CA4',
  secondaryHover: '#EEE2F1',
  infoText: '#414141',

  // Dark Blue
  plum: '#251546',
  charcoal: {
    soft: '#8f93b3',
    light: '#dddfe8',
  },

  // Turbine Red
  red: {
    0: '#f9dde3',
    50: '#f6cdd6',
    100: '#f1acbb',
    200: '#ec8ca0',
    300: '#e76b85',
    400: '#e24b6a',
    500: '#da1a42',
    600: '#c4173b',
    700: '#ae1535',
    800: '#99122e',
    900: '#831028',
  },

  // Ignite Orange
  orange: {
    0: '#FFF1EC',
    50: '#FFE9E0',
    100: '#FFDACC',
    200: '#FFC2AD',
    300: '#FF9A73',
    400: '#FF6E3D',
    500: '#F04C0A',
    600: '#D03A08',
    700: '#AF2807',
    800: '#8F1505',
    900: '#6E0303',
  },

  // Pink
  pink: {
    0: '#FEF2FB',
    50: '#FEEBF9',
    100: '#FDDFF5',
    200: '#FCBFEA',
    300: '#F78ED8',
    400: '#E64FB8',
    500: '#CC0E93',
    600: '#B00C7F',
    700: '#930A6A',
    800: '#770756',
    900: '#5A0541',
  },

  // Plum (900 = Dark Plum)
  darkPlum: {
    0: '#FBF1FB',
    50: '#F8E8F8',
    100: '#F2D5F2',
    200: '#E1A6E1',
    300: '#CB79CF',
    400: '#A442A9',
    500: '#882E8D',
    600: '#732277',
    700: '#5F1862',
    800: '#571264',
    900: '#4F0B65',
  },

  //  Monetization Purple
  purple: {
    0: '#F7F2FC',
    50: '#F2EAFB',
    100: '#E7D9F7',
    200: '#D3B8F1',
    300: '#B688E7',
    400: '#9C5EDE',
    500: '#8336D6',
    600: '#732DC0',
    700: '#6324AA',
    800: '#521D8D',
    900: '#421772',
  },

  // Yellow
  yellow: {
    0: '#FFF6E5',
    50: '#FFF0D4',
    100: '#FFE9BE',
    200: '#FFDFA3',
    300: '#FFD079',
    400: '#FFB733',
    500: '#FFA500',
    600: '#E68E00',
    700: '#CC7000',
    800: '#A85100',
    900: '#803A00',
  },

  // Grass Blade
  green: {
    0: '#ebf3e1',
    50: '#e0ecd1',
    100: '#d3e4be',
    200: '#c6dcaa',
    300: '#b9d597',
    400: '#abcc82',
    500: '#99c167',
    600: '#5e7f34',
    700: '#55722f',
    800: '#425924',
    900: '#384c1f',
  },

  // Grays
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eaeaea',
    300: '#e1e1e1',
    400: '#cacaca',
    500: '#b3b3b3',
    600: '#8e8e8e',
    700: '#6e6e6e',
    800: '#4b4b4b',
    900: '#2c2c2c',
  },
}

// https://material.io/design/motion/speed.html#duration
export const transitions = {
  sm: '100ms',
  md: '200ms',
  lg: '300ms',
}

export const shadows = {
  low: '0px 1px 2px rgba(0, 0, 0, 0.04);',
  medium: '0px 2px 2px rgba(0, 0, 0, 0.02), 0px 2px 8px rgba(0, 0, 0, 0.08);',
  high: '0px 2px 2px rgba(0, 0, 0, 0.02), 0px 2px 14px rgba(0, 0, 0, 0.14);',
}

export const theme = createTheme({
  palette: {
    primary: { main: colors.purple[700], contrastText: colors.white },
    secondary: { main: colors.purple[700], contrastText: colors.white },
    error: { main: colors.red[500] },
    warning: { main: colors.yellow[500] },
    info: { main: colors.purple[700] },
    background: { default: colors.gray[100], paper: colors.white },
    text: { primary: colors.softBlack, secondary: colors.gray[700] },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          '& a': {
            color: colors.accent,
            textDecoration: 'underline',
          },
        },
        standardSuccess: {
          backgroundColor: colors.green[50],
          color: colors.green[900],
          border: `1px solid ${colors.green[500]}`,
        },
        standardError: {
          backgroundColor: colors.red[0],
          color: colors.red[900],
          border: `1px solid ${colors.red[500]}`,
        },
        standardWarning: {
          backgroundColor: colors.yellow[0],
          color: colors.yellow[900],
          border: `1px solid ${colors.yellow[500]}`,
        },
        standardInfo: {
          backgroundColor: colors.gray[100],
          color: colors.infoText,
          border: `1px solid ${colors.charcoal.soft}`,
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontWeight: 600,
          letterSpacing: 0.2,
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: colors.purple[900],
          },
          '&.Mui-disabled': {
            backgroundColor: colors.gray[100],
            color: colors.gray[600],
          },
        },
        outlined: {
          borderColor: colors.accent,
          color: colors.accent,
          '&:hover': {
            borderColor: colors.accent,
            backgroundColor: colors.secondaryHover,
          },
          '&.Mui-disabled': {
            borderColor: colors.charcoal.light,
            color: colors.gray[600],
          },
        },
        text: {
          color: colors.purple[700],
          '&:hover': {
            color: colors.purple[900],
            backgroundColor: colors.secondaryHover,
          },
          '&.Mui-disabled': {
            color: colors.gray[600],
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          '&.Mui-checked': { color: colors.purple[500] },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          height: 'auto',
          padding: '4px 8px',
          borderRadius: 4,
        },
        label: {
          padding: 0,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
        },
      },
    },

    MuiContainer: {
      defaultProps: {
        maxWidth: 'xl',
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            fontSize: 14,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 16,
          color: colors.softBlack,
          '&.Mui-focused': {
            color: colors.accent,
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        colorPrimary: {
          '&.Mui-checked': {
            color: colors.purple[500],
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        colorPrimary: {
          '&.Mui-checked': {
            color: colors.purple[500],
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.purple[50],
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          padding: 16,
          fontWeight: 600,
          backgroundColor: 'white',
          '&:first-of-type': {
            paddingLeft: 24,
          },
          '&:last-of-type': {
            paddingRight: 24,
          },
        },
        root: {
          padding: '12px 16px',
          '&:first-of-type': {
            paddingLeft: 24,
          },
          '&:last-of-type': {
            paddingRight: 24,
          },
        },
      },
    },
  },
})
